import React ,{ useState, useEffect }from 'react'
import TextField from '@mui/material/TextField';
import HelpText from '../tags/HelpText'
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { isNotEmpty, requires, hasCharCountLessThanOrEqualTo, hasCharCountEqualTo, isValidUSI, validateCheckCharacter } from '../../helpers/validation'
import * as Yup from 'yup';

const dateFormat = { year: 'numeric', month: 'short', day: 'numeric' }

const isFirstFamilyNameIndicatorY = context => context.firstFamilyNameIndicator === 'Y'
const isFirstFamilyNameIndicatorN = context => context.firstFamilyNameIndicator === 'N'

const UsiDetailsForm = props => {
const validationSchema = Yup.object().shape({
    legalFirstName: Yup.string()
        .when('firstFamilyNameIndicator', {
          is: (value) => isFirstFamilyNameIndicatorY(value),
          then: (schema) => {
            return schema.test(
              'isNotEmpty',
              'Please provide a First Name.',
              (value, context) => isNotEmpty(value)
            )
          }
        })
        .when('firstFamilyNameIndicator', {
          is: (value) => isFirstFamilyNameIndicatorY(value),
          then: (schema) => {
            return schema.test(
              'isNotEmpty',
              'First Name must be less than or equal to 40 characters long.',
              (value, context) => hasCharCountLessThanOrEqualTo(value, 40)
            )
          }
        }),
    firstFamilyNameIndicator: Yup.string().oneOf(['Y', 'N']),
    singleName: Yup.string()
        .when('firstFamilyNameIndicator', {
          is: (value) => isFirstFamilyNameIndicatorN(value),
          then: (schema) => {
            return schema.test(
              'isNotEmpty',
              'Please provide a Single Name.',
              (value, context) => isNotEmpty(value)
            )
          }
        }),
    usiNo: Yup.string()
      .required('Please provide Unique Student Identifier.')
      .test('charCount', 'Unique Student Identifier must be 10 characters long.', (value, context) => hasCharCountEqualTo(value, 10))
      .test('isValidUSI', 'Please input only A-H, J-N, P-Z, or 2-9.', (value, context) => isValidUSI(value))
      .test('validateCheckCharacter', 'Please enter a Valid USI.', (value, context) => validateCheckCharacter(value)),
  });

  const [form, setForm] = useState({
    dateOfBirth: '',
    singleName: '',
    firstName: '',
    familyName: '',
    usiNo: '',
    firstFamilyNameIndicator: 'N',
    usiVerified: false
  })
  const [errors, setErrors] = useState({});

  useEffect(()=>{
    setForm(props.values)
  },[])

  const handleChange = async (e) => {
    const { name, value } = e.target;
    
    setForm({...form, [name]: value});

    try {
      await validationSchema.validateAt(name, { ...form, [name]: value });
      setErrors({...errors, [name]: undefined});
    } catch (validationError) {
      setErrors({...errors,[name]: validationError.message});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await validationSchema.validate(form, { abortEarly: false });
      setErrors({});
     
      props.handleSubmit(form)
     
    } catch (validationErrors) {
      const formattedErrors = validationErrors.inner.reduce((acc, error) => {
        return { ...acc, [error.path]: error.message };
      }, {});
      setErrors(formattedErrors);
    }
  };

  return !form.usiVerified ? (
    <form onSubmit={handleSubmit}>
      <div className="question-group">
        <HelpText tag="H3" text={'Do you have a first name and family name?'} />
        <RadioGroup
            className="form-item"
            name={'firstFamilyNameIndicator'}
            onChange={handleChange}
            value={form.firstFamilyNameIndicator}
          >
            <FormControlLabel
              label="Yes"
              value={"Y"}
              control={<Radio />}
            />
            <FormControlLabel 
              label="No, I only have one name"
              value={"N"}
              control={<Radio />}
            />
          </RadioGroup>

          { form.firstFamilyNameIndicator === 'Y' ? 
          (
            <div className="row">
              <div className="col-xs-12 col-md-6">
                <h3>First Name</h3>
                <TextField 
                  name='legalFirstName'
                  fullWidth 
                  value={form.legalFirstName} 
                  variant='standard'
                  disabled 
                />
              </div>

              <div className="col-xs-12 col-md-6">
                <h3>Family Name</h3>
                <TextField 
                  name='legalLastName'
                  fullWidth 
                  value={form.legalLastName} 
                  variant='standard'
                  disabled 
                />
              </div>
            </div>
          )
          : (
            <div className="row">
              <div className="col-xs-12 col-md-6">
                <h3>Single Name</h3>
                <TextField
                  name='singleName'
                  error={!!errors.singleName}
                  helperText={errors.singleName}
                  fullWidth
                  onChange={handleChange}
                  value={form.singleName}
                  variant='standard'
                />
              </div>
            </div>
          )
        }
      </div>

      <div className="question-group">
        <div className="row">
          <div className="col-xs-12 col-md-6">
            <h3>Date of Birth</h3>
              <TextField 
                fullWidth 
                value={new Date(form.dateOfBirth).toLocaleDateString('en-AU', { timeZone: 'Australia/Melbourne' }, dateFormat)} 
                disabled
                variant='standard'
              />
          </div>
        </div>
      </div>

      <div className="question-group">
        <div className="row">
          <div className="col-xs-12 col-md-6">
            <h3>Unique Student Identifier(USI)</h3>
            <TextField
              name='usiNo'
              error={!!errors.usiNo}
              helperText={errors.usiNo}
              onChange={handleChange}
              label="Enter your 10 digit Unique Student Identifier (USI)"
              fullWidth
              value={form.usiNo || ''}
              variant='standard'
            />
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-xs-12 col-md-6">
          {props.saving ? (
            <button className="btn btn-lg btn-primary" disabled>
              Verifying...
            </button>
          ) : (
            <button className="btn btn-lg btn-primary">Verify</button>
          )}
        </div>

        <div className="col-xs-12 col-md-6">
          <div className={props.usiVerificationSuccess ? 'show loader' : 'hide loader'}>
            <svg className="circular">
              <circle className="path" cx="50" cy="50" r="20" fill="none" strokeWidth="2" strokeMiterlimit="10" />
            </svg>
            <svg className="suc">
              <path className="checkmark__check" fill="none" d="M10.7,20.4l5.3,5.3l12.4-12.5" />
            </svg>
          </div>
        </div>
      </div>
    </form>
  ) : (
    <div>
      <p>
        <b>Your USI has been validated.</b>
      </p>

      <div className="row">
        <div className="col-xs-12 col-md-6">
          <h3>Unique Student Identifier(USI)</h3>
          <TextField 
            disabled
            fullWidth 
            value={form.usiNo} 
            variant='standard' />
        </div>
      </div>
    </div>
  )
}
export default UsiDetailsForm
